import React, { useState, useEffect, useMemo } from 'react';
import { FiUser, FiUserPlus, FiEdit2, FiTrash2, FiSearch, FiShield, FiMail, FiPhone, FiLock, FiCalendar } from 'react-icons/fi';
import Card from '../../components/Card/Card';
import Badge from '../../components/Badge/Badge';
import Button from '../../components/Button/Button';
import DataTable from '../../components/DataTable/DataTable';
import Modal from '../../components/Modal/Modal';
import Input from '../../components/Input/Input';
import Select from '../../components/Select/Select';
import api from '../../api';
import './Users.css';

const INITIAL_FORM = {
    Username: '', FullName: '', Email: '', Phone: '', Role: 'Librarian', Status: 'Active', Password: ''
};

const Users = ({ showAlert, showConfirm }) => {
    const [users, setUsers] = useState([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(INITIAL_FORM);
    const [sortConfig, setSortConfig] = useState({ key: 'FullName', direction: 'asc' });

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const res = await api.get('/users.php');
            console.log(res.data);
            setUsers(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            showAlert("Sync Error", err.response?.data?.error || "Failed to load user list.", "confirm");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchUsers(); }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(p => ({ ...p, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Basic validation
        if (!form.Username || !form.FullName || (!editingId && !form.Password)) {
            showAlert("Required fields", "Please fill valid Username, Full Name and Password.", "confirm");
            return;
        }

        try {
            if (editingId) {
                await api.put('/users.php', { ...form, UserId: editingId });
                showAlert("Success", "User updated successfully!", "success");
            } else {
                await api.post('/users.php', form);
                showAlert("Success", "New staff account created!", "success");
            }
            setShowModal(false);
            setEditingId(null);
            setForm(INITIAL_FORM);
            fetchUsers();
        } catch (err) {
            showAlert("Process failed", err.response?.data?.error || "Action could not be completed.", "confirm");
        }
    };

    const handleDelete = async (id) => {
        showConfirm("Delete Account", "Are you sure you want to revoke this staff member's access? This action is permanent.", async () => {
            try {
                await api.delete(`/users.php?id=${id}`);
                fetchUsers();
                showAlert("Deleted", "User account removed.", "success");
            } catch (err) {
                showAlert("Delete failed", err.response?.data?.error || "Could not delete user. Security restriction may apply.", "confirm");
            }
        });
    };

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const filtered = useMemo(() => {
        let data = users.filter(u =>
            u.FullName?.toLowerCase().includes(search.toLowerCase()) ||
            u.Username?.toLowerCase().includes(search.toLowerCase()) ||
            u.Email?.toLowerCase().includes(search.toLowerCase())
        );

        if (sortConfig.key) {
            data.sort((a, b) => {
                const aVal = a[sortConfig.key] || '';
                const bVal = b[sortConfig.key] || '';
                if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return data;
    }, [users, search, sortConfig]);

    const columns = [
        { key: 'FullName', label: 'User Details', sortable: true, render: (val, row) => (
            <div className="user-profile-cell">
                <div className="user-avatar-circle">{val?.charAt(0)}</div>
                <div className="user-meta-stack">
                    <span className="user-full-name">{val}</span>
                    <span className="user-username">@{row.Username}</span>
                </div>
            </div>
        )},
        { key: 'Role', label: 'Role', sortable: true, render: (val) => (
            <Badge variant={val === 'Admin' ? 'primary' : 'neutral'} icon={<FiShield />}>
                {val}
            </Badge>
        )},
        { key: 'Contact', label: 'Contact Info', render: (_, row) => (
            <div className="contact-stack">
                <p><FiMail /> {row.Email || '—'}</p>
                <p><FiPhone /> {row.Phone || '—'}</p>
            </div>
        )},
        { key: 'Status', label: 'Status', sortable: true, render: (val) => (
            <Badge variant={val === 'Active' ? 'success' : 'danger'} dot>
                {val}
            </Badge>
        )},
        { key: 'CreatedAt', label: 'Joined On', sortable: true, render: (val) => (
            <span className="date-cell"><FiCalendar /> {new Date(val).toLocaleDateString()}</span>
        )},
        { key: 'actions', label: 'Actions', render: (_, row) => (
            <div className="table-actions">
                <button className="table-action-btn table-action-btn--edit" onClick={() => { setEditingId(row.UserId); setForm({ ...row, Password: '' }); setShowModal(true); }}><FiEdit2 /></button>
                <button className="table-action-btn table-action-btn--delete" onClick={() => handleDelete(row.UserId)}><FiTrash2 /></button>
            </div>
        )}
    ];

    return (
        <div className="users-page">
            <div className="page-header">
                <div>
                    <h2 className="page-header__title">User Management</h2>
                    <p className="page-header__sub">Control system access and professional permissions</p>
                </div>
                <Button variant="primary" icon={<FiUserPlus />} onClick={() => { setEditingId(null); setForm(INITIAL_FORM); setShowModal(true); }}>Add Staff Account</Button>
            </div>

            <Card padding="none">
                <div className="shared-toolbar">
                    <div className="shared-search-box">
                        <FiSearch className="shared-search-box__icon" />
                        <input
                            type="text"
                            placeholder="Search by name, username or email..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="shared-search-box__input"
                        />
                    </div>
                </div>
                <DataTable columns={columns} data={filtered} loading={loading} onSort={handleSort} sortConfig={sortConfig} />
            </Card>

            <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editingId ? 'Edit Staff Profile' : 'Provision New Account'}>
                <form onSubmit={handleSubmit} className="professional-form">
                    <div className="form-grid form-grid--2">
                        <Input id="Username" name="Username" label="Username" value={form.Username} onChange={handleChange} required prefix={<FiUser />} />
                        <Input id="Password" name="Password" label={editingId ? "Update Password (leave empty to keep current)" : "Password"} type="password" value={form.Password} onChange={handleChange} required={!editingId} prefix={<FiLock />} />
                        <Input id="FullName" name="FullName" label="Full Professional Name" value={form.FullName} onChange={handleChange} required className="form-grid__span-2" />
                        <Input id="Email" name="Email" label="Email Address" type="email" value={form.Email} onChange={handleChange} required prefix={<FiMail />} />
                        <Input id="Phone" name="Phone" label="Contact Number" value={form.Phone} onChange={handleChange} prefix={<FiPhone />} />
                        <Select id="Role" name="Role" label="Security Role" options={[{ value: 'Librarian', label: 'Librarian' }, { value: 'Admin', label: 'Administrator' }]} value={form.Role} onChange={handleChange} />
                        <Select id="Status" name="Status" label="Account Status" options={[{ value: 'Active', label: 'Active' }, { value: 'Inactive', label: 'Inactive' }]} value={form.Status} onChange={handleChange} />
                    </div>
                    <div className="form-actions-bar">
                        <Button type="submit" variant="primary" icon={editingId ? <FiEdit2 /> : <FiUserPlus />}>{editingId ? 'Save Changes' : 'Invite User'}</Button>
                        <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default Users;
